import {
  Button,
  Box,
  Modal,
  Checkbox,
  Textarea,
  ModalBody,
  Text,
  ModalContent,
  ModalHeader,
  Flex,
  Input,
  TextLabel,
  ModalFooter,
  Select,
  Space,
  Grid,
  Option,
  ModalOverlay,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Spinner,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import {
  DlpExpressionsModalNameEnum,
  DlpExpressionsModalState,
  ExpressionCriteriaEnum,
} from '../../type';
import { useEffect, useState, useRef } from 'react';
import {
  DlpExpressionDetail,
  TmDlpExpressionValidator,
} from '@Policy/utils/types';
import i18next, { getFixedT } from 'i18next';
import { PolicyApi } from '@Apis';
import HightLightContent from '@Components/Common/HighLightContent';
import { PromiseResult } from '@Types/index';
import { usePermissionStore } from '../../../../../../models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { DBCSString } from '@Utils/dbcsString';
import { ModalInlineToast } from '../../../../../../components/ModalInlineToast';

const initExpressionDetail: DlpExpressionDetail = {
  dpExpressionName: '', //not empty
  dpExpressionDesc: '',
  dpExpressionType: ExpressionCriteriaEnum.None,
  dpExpressionPattern: '', //not empty
  dpExpressionCasesensitive: 0,
  dpExpressionDisplayformat: '',
  dpExpressionExample: '',
  dpExpressionAlphabetcharset: '',
  dpExpressionMinlength: 0,
  dpExpressionMaxlength: 0,
  dpExpressionSuffixlength: 0,
  dpExpressionMinleftradius: 0,
  dpExpressionMaxleftradius: 0,
  dpExpressionMaxrightradius: 0,
  dpExpressionValidator: 'None',
  dpExpressionValidator_val: 'None',
};
const initValidatorList: TmDlpExpressionValidator[] = [
  {
    dictId: 5257,
    dictClassify: 'Validation',
    dictNo: 1,
    dictKey: 'None',
    dictValue: '1',
    dictDisplayvalue: 'None',
  },
];

enum InlineToastName {
  POOR_PERFORMANCE = 'POLICY_API;EXPRESSION_POOR_PERFORMANCE',
  ANALYSIS_FAILED = 'POLICY_API;EXPRESSION_ANALYSIS_FAILED',
  OTHER = 'other',
}

export const EditOrAddModal = ({
  modalState,
  onClose,
  refreshTable,
}: {
  modalState: DlpExpressionsModalState;
  onClose: () => void;
  refreshTable: () => void;
}) => {
  const { widthEditablePermission } = usePermissionStore();
  const dlpEditPermission = widthEditablePermission(
    PermissionNameEnum.dlpPolicy
  );
  const { t } = useTranslation(['common']);
  const expressionT = getFixedT(
    null,
    'policy',
    'policy.dataIdentifiers.expressions'
  );
  const [formData, setFormData] =
    useState<DlpExpressionDetail>(initExpressionDetail);
  const disabled = formData.dpExpressionBuiltin === 1 || !dlpEditPermission;

  const [validatorList, setValidatorList] = useState(initValidatorList);
  const [error, setError] = useState({
    dpExpressionName: false,
    dpExpressionPattern: false,
    dpExpressionAlphabetcharset: false,
  });
  // const [loading, setLoading] = useState(false);

  const charsetLabelString = {
    [ExpressionCriteriaEnum.None]: '',
    [ExpressionCriteriaEnum.SpecificCharacters]:
      expressionT('label.characters'),
    [ExpressionCriteriaEnum.Suffix]: expressionT('label.specificCharacters'),
    [ExpressionCriteriaEnum.SingleCharacterSeparator]:
      expressionT('label.separator'),
  }[formData.dpExpressionType as ExpressionCriteriaEnum];

  const getInputAttr = (key: string) => {
    const data = formData[key as keyof typeof formData];
    const dataType = typeof data;
    return {
      disabled,
      value: data + '',
      onChange: (e: any) => {
        if (Object.keys(error).includes(key)) {
          setError({
            ...error,
            [key]: false,
          });
        }
        let value: string | number = e.target.value;
        if (dataType === 'number') {
          value = Math.max(0, Math.min(99999999, Number(e.target.value)));
        }
        setFormData({
          ...formData,
          [key]: value,
        });
      },
      ...(dataType === 'number' && {
        type: 'number',
        min: '0',
        max: '999999999', //maxLength: 9
      }),
    };
  };

  useEffect(() => {
    const cfgIndex = modalState?.data?.cfgIndex;
    const fetchData = async () => {
      if (cfgIndex) {
        //update Expression
        const [r1, r2] = await Promise.allSettled([
          PolicyApi.getDlpExpressionById(cfgIndex, i18next.language),
          PolicyApi.getDlpValidatorsList(i18next.language),
        ]);

        let _validatorList = initValidatorList;
        if (r2.status === PromiseResult.Fulfilled) {
          _validatorList = r2.value.data;
        }
        setValidatorList(_validatorList);

        if (r1.status === PromiseResult.Fulfilled) {
          setFormData({
            ...initExpressionDetail,
            ...r1.value.data,
            dpExpressionValidator_val:
              _validatorList.find(
                (item) =>
                  item['dictKey'] === r1.value.data.dpExpressionValidator
              ) || 'None',
          });
        } else {
          setFormData(initExpressionDetail);
        }
      } else {
        //create Expression
        try {
          const res = await PolicyApi.getDlpValidatorsList(i18next.language);
          setValidatorList(res.data);
        } catch {
          setValidatorList(initValidatorList);
        }
      }
    };
    fetchData();
  }, [modalState?.data?.cfgIndex]);

  const toasts = {
    [InlineToastName.POOR_PERFORMANCE]: {
      appearance: 'error' as const,
      content: (
        <>
          <Text>
            {t('errors.policy.POLICY_API;EXPRESSION_POOR_PERFORMANCE')}
          </Text>
        </>
      ),
      duration: 5000,
    },
    [InlineToastName.ANALYSIS_FAILED]: {
      appearance: 'error' as const,
      content: (
        <>
          <Text>
            {t('errors.policy.POLICY_API;EXPRESSION_ANALYSIS_FAILED')}
          </Text>
        </>
      ),
      duration: 5000,
    },
    [InlineToastName.OTHER]: {
      appearance: 'error' as const,
      content: (
        <>
          <Text>{t('errors.internalError')}</Text>
        </>
      ),
      duration: 5000,
    },
  };

  const modalRef = useRef<{
    notify: (name: string /* keyof typeof toasts */) => void;
  }>(null);

  const onSave = async () => {
    try {
      const { scanCostTimeInMilliseconds } = (
        await PolicyApi.analysisExpression({
          dpExpressionBuiltin: 0,
          dpExpressionCasesensitive: formData.dpExpressionCasesensitive,
          dpExpressionDesc: formData.dpExpressionDesc,
          dpExpressionMaxleftradius: formData.dpExpressionMaxleftradius,
          dpExpressionMaxlength: formData.dpExpressionMaxlength,
          dpExpressionMaxrightradius: formData.dpExpressionMaxrightradius,
          dpExpressionMinleftradius: formData.dpExpressionMinleftradius,
          dpExpressionMinlength: formData.dpExpressionMinlength,
          dpExpressionName: formData.dpExpressionName,
          dpExpressionPattern: formData.dpExpressionPattern,
          dpExpressionSuffixlength: formData.dpExpressionSuffixlength,
          dpExpressionType: formData.dpExpressionType,
          dpExpressionValidator: formData.dpExpressionValidator,
        })
      ).data;
      console.log('scanCostTimeInMilliseconds: ' + scanCostTimeInMilliseconds);
      if (scanCostTimeInMilliseconds > 10000) {
        modalRef.current?.notify(InlineToastName.POOR_PERFORMANCE);
        return;
      }
    } catch (e: any) {
      const errorCode = e?.data?.error?.details?.[0]?.errorCode;
      if (
        [
          InlineToastName.ANALYSIS_FAILED,
          InlineToastName.POOR_PERFORMANCE,
        ].includes(errorCode)
      ) {
        modalRef.current?.notify(errorCode);
      } else {
        modalRef.current?.notify(InlineToastName.OTHER);
      }
      return;
    }

    if (
      formData.dpExpressionName === '' ||
      formData.dpExpressionPattern === '' ||
      (formData.dpExpressionType !== ExpressionCriteriaEnum.None &&
        formData.dpExpressionAlphabetcharset === '')
    ) {
      setError({
        dpExpressionName: formData.dpExpressionName === '',
        dpExpressionPattern: formData.dpExpressionPattern === '',
        dpExpressionAlphabetcharset:
          formData.dpExpressionType !== ExpressionCriteriaEnum.None &&
          formData.dpExpressionAlphabetcharset === '',
      });
      return;
    }
    try {
      const cfgIndex = modalState?.data?.cfgIndex;
      if (cfgIndex) {
        await PolicyApi.updateDlpExpressionById(cfgIndex, formData);
      } else {
        await PolicyApi.addDlpExpression(formData);
      }
      window.showNotification({
        titleMsg: expressionT('saveSuccessfully'),
        severity: 'success',
      });
      onClose();
      refreshTable();
    } catch (e) {
      window.showNotification({
        titleMsg: t('common.unknownError'),
        severity: 'error',
      });
    }
  };

  return (
    <Modal
      ensureFocus={true}
      autoFocus={false}
      closeOnEsc={true}
      closeOnOutsideClick={false}
      isClosable={true}
      isOpen={
        modalState.name === DlpExpressionsModalNameEnum.Add ||
        modalState.name === DlpExpressionsModalNameEnum.Edit
      }
      onClose={onClose}
      size="lg"
    >
      <ModalOverlay opacity={0.9} />
      <ModalContent>
        <ModalInlineToast toasts={toasts} ref={modalRef} />
        {/* edit or add */}
        <ModalHeader>{expressionT(`title.${modalState.name}`)}</ModalHeader>
        <ModalBody>
          <Text fontWeight="600" fontSize="18px" lineHeight="24px">
            {expressionT('basicProp')}
          </Text>
          <Grid
            gridTemplateColumns="25% 75%"
            gridColumnGap="4px"
            gridRowGap="8px"
            pt="16px"
          >
            <TextLabel className="required">
              {expressionT('label.dpExpressionName')}:
            </TextLabel>
            <Input
              maxLength={256}
              {...getInputAttr('dpExpressionName')}
              error={error.dpExpressionName}
            />
            <TextLabel>{expressionT('label.dpExpressionDesc')}:</TextLabel>
            <Input {...getInputAttr('dpExpressionDesc')} maxLength={512} />
            <TextLabel>{expressionT('label.dpExpressionType')}:</TextLabel>
            <Select
              variant="outline"
              value={formData.dpExpressionType}
              onChange={(e: any) => {
                setFormData({
                  ...formData,
                  dpExpressionType: e.target.value as ExpressionCriteriaEnum,
                });
              }}
              disabled={disabled}
            >
              <Option value={ExpressionCriteriaEnum.None}>
                {expressionT('typeList.none')}
              </Option>
              <Option value={ExpressionCriteriaEnum.SpecificCharacters}>
                {expressionT('typeList.specificCharacters')}
              </Option>
              <Option value={ExpressionCriteriaEnum.Suffix}>
                {expressionT('typeList.suffix')}
              </Option>
              <Option value={ExpressionCriteriaEnum.SingleCharacterSeparator}>
                {expressionT('typeList.singleCharacterSeparator')}
              </Option>
            </Select>
            {formData.dpExpressionType !== ExpressionCriteriaEnum.None && (
              <>
                <TextLabel className="required">
                  {charsetLabelString}:
                </TextLabel>
                <Input
                  {...getInputAttr('dpExpressionAlphabetcharset')}
                  error={error.dpExpressionAlphabetcharset}
                />
                {formData.dpExpressionType ===
                  ExpressionCriteriaEnum.Suffix && (
                  <>
                    <TextLabel>
                      {expressionT('label.dpExpressionSuffixlength')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionSuffixlength')} />
                  </>
                )}
                {formData.dpExpressionType !==
                  ExpressionCriteriaEnum.SingleCharacterSeparator && (
                  <>
                    <TextLabel>
                      {expressionT('label.dpExpressionMinlength')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionMinlength')} />
                    <TextLabel>
                      {expressionT('label.dpExpressionMaxlength')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionMaxlength')} />
                  </>
                )}
                {formData.dpExpressionType ===
                  ExpressionCriteriaEnum.SingleCharacterSeparator && (
                  <>
                    <TextLabel>
                      {expressionT('label.dpExpressionMinleftradius')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionMinleftradius')} />
                    <TextLabel>
                      {expressionT('label.dpExpressionMaxleftradius')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionMaxleftradius')} />
                    <TextLabel>
                      {expressionT('label.dpExpressionMaxrightradius')}:
                    </TextLabel>
                    <Input {...getInputAttr('dpExpressionMaxrightradius')} />
                  </>
                )}
              </>
            )}
            <TextLabel className="required">
              {expressionT('label.dpExpressionPattern')}:
            </TextLabel>
            <Grid gridTemplateColumns="auto max-content" gridColumnGap="8px">
              <Input
                {...getInputAttr('dpExpressionPattern')}
                error={error.dpExpressionPattern}
              />
              <Checkbox
                checked={formData.dpExpressionCasesensitive === 1}
                onChange={(e: any) =>
                  setFormData({
                    ...formData,
                    dpExpressionCasesensitive: e.target.checked ? 1 : 0,
                  })
                }
                disabled={disabled}
              >
                {expressionT('label.caseSensitive')}
              </Checkbox>
            </Grid>
            <TextLabel>
              {expressionT('label.dpExpressionDisplayformat')}:
            </TextLabel>
            <Input {...getInputAttr('dpExpressionDisplayformat')} />
            <TextLabel>{expressionT('label.dpExpressionExample')}:</TextLabel>
            <Input {...getInputAttr('dpExpressionExample')} />
            <TextLabel>{expressionT('label.dpExpressionValidator')}:</TextLabel>
            <Select
              value={JSON.stringify({
                key: formData.dpExpressionValidator,
                val: formData.dpExpressionValidator_val,
              })}
              onChange={(e: any) => {
                const v = JSON.parse(e.target.value) as {
                  key: string;
                  val: TmDlpExpressionValidator | string;
                };
                setFormData({
                  ...formData,
                  dpExpressionValidator: v.key,
                  dpExpressionValidator_val: v.val,
                });
              }}
              disabled={disabled}
            >
              <Option
                value={JSON.stringify({
                  key: validatorList[0].dictKey,
                  val: validatorList[0].dictDisplayvalue,
                })}
              >
                {validatorList[0].dictDisplayvalue}
              </Option>
              {validatorList.slice(1).map((item) => (
                <Option
                  value={JSON.stringify({
                    key: item.dictKey,
                    val: item,
                  })}
                >
                  {item.dictDisplayvalue}
                </Option>
              ))}
            </Select>
          </Grid>
          <ExpressionTestForm formData={formData} />
        </ModalBody>
        {/* </Scrollbar> */}
        <ModalFooter>
          <SaveBtn disabled={disabled} onClick={onSave} />
          <Space width="2x" />
          <Button minWidth="20x" onClick={onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const ExpressionTestForm = ({
  formData,
}: {
  formData: DlpExpressionDetail;
}) => {
  const { t } = useTranslation(['common']);
  const expressionT = getFixedT(
    null,
    'policy',
    'policy.dataIdentifiers.expressions'
  );

  const [testForm, setTestForm] = useState<{
    testData: string;
    testResult: React.ReactNode;
  }>({
    testData: '',
    testResult: '',
  });

  const onTest = async () => {
    const type = (
      {
        [ExpressionCriteriaEnum.None]: 1,
        [ExpressionCriteriaEnum.SpecificCharacters]: 2,
        [ExpressionCriteriaEnum.Suffix]: 3,
        [ExpressionCriteriaEnum.SingleCharacterSeparator]: 4,
      } as const
    )[formData.dpExpressionType];

    const payload = {
      expression: {
        ID: 0,
        name: formData.dpExpressionName,
        pattern: formData.dpExpressionPattern,
        type,
        validationEntityType:
          typeof formData.dpExpressionValidator_val === 'string'
            ? 1
            : Number.parseInt(formData.dpExpressionValidator_val.dictValue, 10),
        isPatternCaseSensitive: formData.dpExpressionCasesensitive,
        alphabet: formData.dpExpressionAlphabetcharset,
        minlen: formData.dpExpressionMinlength,
        maxlen: formData.dpExpressionMaxlength,
        endlen: formData.dpExpressionSuffixlength,
        l_radius: formData.dpExpressionMaxleftradius,
        r_radius: formData.dpExpressionMaxrightradius,
        min_l_radius: formData.dpExpressionMinleftradius,
      },
      testData: testForm.testData,
    };
    try {
      const { result, testContent } = (await PolicyApi.testExpression(payload))
        .data;
      //handle test result
      if (result === null || result.length === 0) {
        setTestForm({
          testData: testForm.testData,
          testResult: (
            <>
              <div>
                {expressionT('testResult', {
                  number: 0,
                })}
              </div>
              <HightLightContent
                commonStringArr={[testContent]}
                highlightStringArr={[]}
              />
            </>
          ),
        });
        return;
      }
      const commonStringArr: string[] = [];
      const highlightStringArr: string[] = [];
      let startPosition = 0;
      const sortedDetails = result.sort((a, b) => a.pos - b.pos);
      if (!DBCSString.containsDBCS(testContent)) {
        for (let i = 0; i < sortedDetails.length; i++) {
          const item = sortedDetails[i];
          commonStringArr.push(testContent.slice(startPosition, item.pos));
          highlightStringArr.push(
            testContent.slice(item.pos, item.pos + item.len)
          );
          startPosition = item.pos + item.len;
          if (i === sortedDetails.length - 1) {
            commonStringArr.push(testContent.slice(startPosition));
          }
        }
      } else {
        for (let i = 0; i < sortedDetails.length; i++) {
          const item = sortedDetails[i];
          commonStringArr.push(
            DBCSString.substrUtf8Bytes(
              testContent,
              startPosition,
              item.pos - startPosition
            )
          );
          highlightStringArr.push(
            DBCSString.substrUtf8Bytes(testContent, item.pos, item.len)
          );
          startPosition = item.pos + item.len;
          if (i === sortedDetails.length - 1) {
            commonStringArr.push(
              DBCSString.substrUtf8Bytes(
                testContent,
                startPosition,
                DBCSString.getLengthInBytes(testContent)
              )
            );
          }
        }
      }
      setTestForm({
        testData: testForm.testData,
        testResult: (
          <>
            <div>
              {expressionT('testResult', { number: highlightStringArr.length })}
            </div>
            <HightLightContent
              commonStringArr={commonStringArr}
              highlightStringArr={highlightStringArr}
            />
          </>
        ),
      });
    } catch (e: any) {
      window.showNotification({
        titleMsg: t('common.unknownError'),
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Text fontWeight="600" fontSize="18px" lineHeight="24px">
        {expressionT('testArea')}
      </Text>
      <Grid
        gridTemplateColumns="25% 75%"
        gridColumnGap="4px"
        gridRowGap="8px"
        pt="16px"
      >
        <TextLabel>{expressionT('label.testData')}:</TextLabel>
        <Grid
          gridTemplateColumns="auto 100px"
          gridColumnGap="20px"
          gridRowGap="8px"
        >
          <Textarea
            rows={6}
            value={testForm.testData}
            onChange={(e: any) => {
              setTestForm({
                testData: e.target.value,
                testResult: '',
              });
            }}
          />
          <Flex direction="column" gap="8px">
            <Button
              variant="primary"
              onClick={onTest}
              disabled={testForm.testData === ''}
            >
              {t('common.action.label.test')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setTestForm({
                  testData: '',
                  testResult: '',
                });
              }}
            >
              {t('common.action.label.clear')}
            </Button>
          </Flex>
        </Grid>
        <TextLabel>{expressionT('label.testResult')}:</TextLabel>
        <Box>{testForm.testResult}</Box>
      </Grid>
    </>
  );
};

const SaveBtn = ({
  disabled,
  onClick: onClickProp,
}: {
  disabled: boolean;
  onClick: () => Promise<void>;
}) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const onClick = async () => {
    setLoading(true);
    await onClickProp();
    setLoading(false);
  };

  const props: { isOpen: boolean } | { trigger: 'hover' } = loading
    ? {
        isOpen: true,
      }
    : { trigger: 'hover' };

  // add key to force re-render the popover content
  return (
    <Popover placement="top" {...props} key={loading ? 'true' : 'false'}>
      <PopoverTrigger>
        <Button
          disabled={disabled || loading}
          onClick={onClick}
          variant="primary"
        >
          {loading ? (
            <>
              <Spinner size="xs" />
              {t('common.action.label.saving')}
            </>
          ) : (
            t('common.action.label.save')
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        PopperProps={{
          usePortal: true,
        }}
        maxWidth="320px"
      >
        {loading
          ? t('policy.dataIdentifiers.expressions.savingTooltip')
          : t('policy.dataIdentifiers.expressions.saveTooltip')}
      </PopoverContent>
    </Popover>
  );
};
